// ============================================================================
// MarketingKit — shared design-system components for the Revailo Sales public
// site (revailo-sales.com). Plain-CSS (see ../marketing.css), blue-branded,
// theme-independent. Pages compose these declaratively; most page content is
// just data arrays passed to <FeatureGrid>, <StatBand>, <Accordion>, etc.
//
// Wrap every public page in <MarketingShell active="…">…</MarketingShell>.
//
// Quick API reference (props):
//   <Seo title description path? keywords? jsonLd? />
//   <PageHero eyebrow title lead primary={{label,to}} secondary={{label,to}} align="center|left">
//   <Section id? sm?><Container> … </Container></Section>
//   <SectionIntro eyebrow title lead align="center|left" />
//   <Heading as="h2"> text <Muted>muted tail</Muted> </Heading>
//   <FeatureGrid cols={3} items={[{icon,title,body,tag?}]} />
//   <FeatureRow eyebrow title body bullets={[]} media={<Mock/>} reverse? />
//   <StatBand stats={[{value,label}]} />
//   <Steps items={[{title,body}]} />
//   <Accordion items={[{q,a}]} />
//   <Quote quote name role />
//   <LogoCloud label logos={[]} />
//   <PricingTable plans={[{name,price,period,tagline,features:[],cta:{label,to},featured?,badge?}]} />
//   <Timeline items={[{date,tag?,title,body?,bullets?}]} />
//   <PostGrid posts={[{tag,title,excerpt,author,date,readTime}]} />
//   <JobList groups={[{team,roles:[{title,location,type,to?}]}]} />
//   <StatusPill state="operational|degraded|maintenance|outage" label? />
//   <UptimeBar days={90} incidents={{5:'outage'}} />
//   <CTASection eyebrow title body primary={{label,to}} secondary={{label,to}} />
//   <Prose> … </Prose>  |  <LegalLayout toc={[{id,label}]}> … </LegalLayout>
//   <Mock kind="dashboard|leads|sequence|analytics|inbox" />  (product screenshots)
//   <Button to|href variant="primary|accent|secondary|ghost|white|outline-white" lg?>
//   <Icon name="…" />  (check scale wallet bank layers shield bolt pin chart boxes
//      users arrow plus minus alert receipt sparkle mail phone rocket target map
//      lock refresh filter zap building hammer home clock star globe play)
// ============================================================================
import { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import '../marketing.css'

export const SITE_NAME = 'Revailo Sales'
export const SITE_URL = 'https://revailo-sales.com'

// ── Icons ────────────────────────────────────────────────────────────────────
export function Icon({ name, className = '', size = 20, style }) {
  const p = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  }
  const paths = {
    check: <polyline points="20 6 9 17 4 12" {...p} />,
    scale: (
      <>
        <path d="M12 3v18" {...p} />
        <path d="M5 7h14" {...p} />
        <path d="M5 7l-2.5 6a3 3 0 0 0 5 0L5 7zM19 7l-2.5 6a3 3 0 0 0 5 0L19 7z" {...p} />
        <path d="M8 21h8" {...p} />
      </>
    ),
    wallet: (
      <>
        <rect x="3" y="6" width="18" height="13" rx="2.5" {...p} />
        <path d="M16 12h3" {...p} />
      </>
    ),
    bank: (
      <>
        <path d="M3 9l9-5 9 5" {...p} />
        <path d="M5 9v8M9 9v8M15 9v8M19 9v8" {...p} />
        <path d="M3 21h18" {...p} />
      </>
    ),
    layers: (
      <>
        <path d="M12 3l9 5-9 5-9-5 9-5z" {...p} />
        <path d="M3 13l9 5 9-5" {...p} />
      </>
    ),
    shield: (
      <>
        <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" {...p} />
        <polyline points="9 12 11 14 15 10" {...p} />
      </>
    ),
    bolt: <polygon points="13 2 4 14 11 14 10 22 19 10 12 10 13 2" {...p} />,
    pin: (
      <>
        <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11z" {...p} />
        <circle cx="12" cy="10" r="2.5" {...p} />
      </>
    ),
    chart: (
      <>
        <path d="M4 4v16h16" {...p} />
        <path d="M8 14l3-3 3 2 4-5" {...p} />
      </>
    ),
    boxes: (
      <>
        <rect x="3" y="3" width="8" height="8" rx="1.5" {...p} />
        <rect x="13" y="3" width="8" height="8" rx="1.5" {...p} />
        <rect x="3" y="13" width="8" height="8" rx="1.5" {...p} />
        <rect x="13" y="13" width="8" height="8" rx="1.5" {...p} />
      </>
    ),
    users: (
      <>
        <circle cx="9" cy="8" r="3.2" {...p} />
        <path d="M3 20a6 6 0 0 1 12 0" {...p} />
        <path d="M16 5.5a3.2 3.2 0 0 1 0 6M21 20a6 6 0 0 0-4-5.7" {...p} />
      </>
    ),
    arrow: (
      <>
        <line x1="5" y1="12" x2="19" y2="12" {...p} />
        <polyline points="13 6 19 12 13 18" {...p} />
      </>
    ),
    plus: (
      <>
        <line x1="12" y1="5" x2="12" y2="19" {...p} />
        <line x1="5" y1="12" x2="19" y2="12" {...p} />
      </>
    ),
    minus: <line x1="5" y1="12" x2="19" y2="12" {...p} />,
    alert: (
      <>
        <path d="M12 3l9 16H3l9-16z" {...p} />
        <line x1="12" y1="10" x2="12" y2="14" {...p} />
        <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
      </>
    ),
    receipt: (
      <>
        <path d="M5 3h14v18l-2.5-1.5L14 21l-2-1.5L10 21l-2.5-1.5L5 21V3z" {...p} />
        <path d="M9 8h6M9 12h6" {...p} />
      </>
    ),
    sparkle: <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" {...p} />,
    mail: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2.5" {...p} />
        <path d="m3 7 9 6 9-6" {...p} />
      </>
    ),
    phone: (
      <path
        d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2 4.1 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c1 .3 1.9.6 2.8.7A2 2 0 0 1 22 16.9z"
        {...p}
      />
    ),
    rocket: (
      <>
        <path d="M5 15c-1.5 1.5-2 5-2 5s3.5-.5 5-2a2.8 2.8 0 0 0-3-3z" {...p} />
        <path d="M12 15l-3-3a12 12 0 0 1 7-9c3 0 5 2 5 5a12 12 0 0 1-9 7z" {...p} />
        <circle cx="15" cy="9" r="1.4" {...p} />
      </>
    ),
    target: (
      <>
        <circle cx="12" cy="12" r="9" {...p} />
        <circle cx="12" cy="12" r="5" {...p} />
        <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      </>
    ),
    map: (
      <>
        <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21 3 6" {...p} />
        <line x1="9" y1="3" x2="9" y2="18" {...p} />
        <line x1="15" y1="6" x2="15" y2="21" {...p} />
      </>
    ),
    lock: (
      <>
        <rect x="4" y="10" width="16" height="11" rx="2.5" {...p} />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" {...p} />
      </>
    ),
    refresh: (
      <>
        <path d="M4 12a8 8 0 0 1 13.7-5.7L20 8" {...p} />
        <path d="M20 4v4h-4" {...p} />
        <path d="M20 12a8 8 0 0 1-13.7 5.7L4 16" {...p} />
        <path d="M4 20v-4h4" {...p} />
      </>
    ),
    filter: <polygon points="3 4 21 4 14 12.5 14 19 10 21 10 12.5 3 4" {...p} />,
    zap: <polygon points="13 2 4 14 11 14 10 22 19 10 12 10 13 2" {...p} />,
    building: (
      <>
        <rect x="4" y="3" width="16" height="18" rx="1.5" {...p} />
        <path d="M9 7h1M14 7h1M9 11h1M14 11h1M9 15h1M14 15h1" {...p} />
        <path d="M10 21v-3h4v3" {...p} />
      </>
    ),
    hammer: (
      <>
        <path d="M14 3l7 7-3 3-7-7 3-3z" {...p} />
        <path d="M10 8l-7 7a2.8 2.8 0 0 0 4 4l7-7" {...p} />
      </>
    ),
    home: (
      <>
        <path d="M3 10 12 3l9 7" {...p} />
        <path d="M5 9v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9" {...p} />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="9" {...p} />
        <polyline points="12 7 12 12 15 14" {...p} />
      </>
    ),
    star: (
      <polygon
        points="12 3 14.6 8.6 20.5 9.3 16 13.3 17.4 19.2 12 16 6.6 19.2 8 13.3 3.5 9.3 9.4 8.6 12 3"
        {...p}
      />
    ),
    globe: (
      <>
        <circle cx="12" cy="12" r="9" {...p} />
        <path d="M3 12h18M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18z" {...p} />
      </>
    ),
    play: <polygon points="7 4 20 12 7 20 7 4" {...p} />,
    doc: (
      <>
        <path d="M6 3h8l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" {...p} />
        <path d="M14 3v5h5M8 13h8M8 17h6" {...p} />
      </>
    ),
    gauge: (
      <>
        <path d="M4 18a8 8 0 1 1 16 0" {...p} />
        <path d="M12 18l4-5" {...p} />
      </>
    ),
  }
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      style={style}
      aria-hidden="true"
    >
      {paths[name] || paths.check}
    </svg>
  )
}

// ── Layout ───────────────────────────────────────────────────────────────────
export function Container({ children, className = '', style }) {
  return (
    <div className={`mx-auto w-full max-w-[1180px] px-5 sm:px-6 ${className}`} style={style}>
      {children}
    </div>
  )
}

export function Section({ children, id, sm = false, className = '', style }) {
  return (
    <section id={id} className={`${sm ? 'py-[56px]' : 'py-[60px] sm:py-[88px]'} ${className}`} style={style}>
      {children}
    </section>
  )
}

export function Eyebrow({ children, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-x-2 text-[12px] font-bold tracking-[0.14em] uppercase text-[#1D68D9] ${className}`}>
      <span className="w-[6px] h-[6px] rounded-full bg-[#3C87F7]" />
      {children}
    </span>
  )
}

export function Muted({ children }) {
  return <span className="text-[#b4bcca]">{children}</span>
}

export function Heading({ children, as: Tag = 'h2', className = '', style }) {
  const cls =
    Tag === 'h1'
      ? 'm-0 text-[38px] min-[720px]:text-[54px] font-semibold tracking-[-0.022em] leading-[1.07] text-[#0f172a]'
      : Tag === 'h3'
        ? 'm-0 text-[22px] min-[720px]:text-[26px] font-semibold tracking-[-0.022em] leading-[1.15] text-[#0f172a]'
        : 'm-0 text-[30px] min-[720px]:text-[42px] font-semibold tracking-[-0.022em] leading-[1.07] text-[#0f172a]'
  return (
    <Tag className={`${cls} ${className}`} style={style}>
      {children}
    </Tag>
  )
}

export function Lead({ children, className = '' }) {
  return (
    <p
      className={`m-0 text-[16px] min-[720px]:text-[18px] leading-[1.6] text-[#64748b] ${className}`}
    >
      {children}
    </p>
  )
}

// ── Buttons ──────────────────────────────────────────────────────────────────
const BTN_VARIANT = {
  primary: 'bg-[#0b1220] text-white hover:bg-[#1e293b]',
  accent: 'bg-[#3C87F7] text-white hover:bg-[#1D68D9]',
  secondary: 'bg-white text-[#0f172a] border-[#e6e9ef] hover:bg-[#fafbfd] hover:border-[#b4bcca]',
  ghost: 'bg-transparent text-[#334155] px-[14px] py-[9px] hover:bg-[#f6f8fb] hover:text-[#0f172a]',
  white: 'bg-white text-[#0b1220] hover:bg-[rgba(255,255,255,0.9)]',
  'outline-white': 'bg-transparent text-white border-[rgba(255,255,255,0.25)] hover:bg-[rgba(255,255,255,0.1)]',
}
export function Button({ to, href, variant = 'primary', lg = false, children, className = '', ...rest }) {
  const cls = `inline-flex items-center justify-center gap-x-2 rounded-full text-[14px] font-semibold px-5 py-3 border border-transparent transition-[background,color,border-color,transform] duration-150 cursor-pointer whitespace-nowrap active:translate-y-[1px] ${BTN_VARIANT[variant] || BTN_VARIANT.primary} ${lg ? 'px-[26px] py-[14px] text-[15px]' : ''} ${className}`
  if (href) {
    return (
      <a href={href} className={cls} {...rest}>
        {children}
      </a>
    )
  }
  return (
    <Link to={to || '/contact'} className={cls} {...rest}>
      {children}
    </Link>
  )
}

// ── Small pieces ─────────────────────────────────────────────────────────────
export function Card({ children, soft = false, hover = false, className = '', style }) {
  return (
    <div
      className={`rounded-[20px] border p-[26px] transition-[border-color,box-shadow,transform] duration-150 ${soft ? 'border-transparent bg-[#f6f8fb]' : 'border-[#e6e9ef] bg-white'} ${hover ? 'hover:border-[#b4bcca] hover:shadow-[0_8px_30px_rgba(15,23,42,0.06)] hover:-translate-y-[2px]' : ''} ${className}`}
      style={style}
    >
      {children}
    </div>
  )
}

export function DarkPanel({ children, className = '', style }) {
  return (
    <div
      className={`relative overflow-hidden rounded-[28px] bg-[#0b1220] text-white ${className}`}
      style={style}
    >
      {children}
    </div>
  )
}

// Each tone owns border/background/text outright. Appending a tone's colours to
// a base that already sets them does NOT override them: two utilities for the
// same property have equal specificity, so the winner is whichever Tailwind
// emits later, not whichever is written last. That is how tone="dark" ended up
// with bg-white AND text-white — an invisible label inside the dark CTA panel.
const BADGE_TONE = {
  neutral: 'border-[#e6e9ef] bg-white text-[#64748b]',
  accent: 'border-[#bfdcff] bg-[#eaf2fe] text-[#1D68D9]',
  dark: 'border-[rgba(255,255,255,0.16)] bg-[rgba(255,255,255,0.1)] text-white',
}

export function Badge({ children, tone = 'neutral' }) {
  return (
    <span
      className={`inline-flex items-center gap-x-[6px] rounded-full border px-[10px] py-[4px] text-[11px] font-semibold ${BADGE_TONE[tone] || BADGE_TONE.neutral}`}
    >
      {children}
    </span>
  )
}

export function GlyphTile({ name, dark = false }) {
  return (
    <span
      className={`inline-flex items-center justify-center w-[44px] h-[44px] rounded-[14px] flex-none ${dark ? 'bg-[rgba(255,255,255,0.1)] text-white' : 'bg-[#eaf2fe] text-[#1D68D9]'}`}
    >
      <Icon name={name} size={22} />
    </span>
  )
}

// ── Reveal on scroll ─────────────────────────────────────────────────────────
export function Reveal({ children, delay = 0, className = '', style }) {
  const ref = useRef(null)
  const [on, setOn] = useState(false)
  useEffect(() => {
    const el = ref.current
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduce || !el || typeof IntersectionObserver !== 'function') {
      setOn(true)
      return undefined
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setOn(true)
          io.disconnect()
        }
      },
      { threshold: 0, rootMargin: '0px 0px -8% 0px' },
    )
    io.observe(el)
    // Fail-open safety net: content must never stay invisible if the observer
    // doesn't fire (edge browsers, no-scroll sessions, headless renderers).
    const t = setTimeout(() => {
      setOn(true)
      io.disconnect()
    }, 1400)
    return () => {
      io.disconnect()
      clearTimeout(t)
    }
  }, [])
  return (
    <div
      ref={ref}
      className={`opacity-0 translate-y-[24px] transition-[opacity,transform] duration-[800ms] ease-[cubic-bezier(0.16,1,0.3,1)] will-change-[opacity,transform] motion-reduce:opacity-100 motion-reduce:translate-y-0 motion-reduce:transition-none ${on ? 'opacity-100 translate-y-0' : ''} ${className}`}
      style={{ ...style, transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  )
}

// ── Composite blocks ─────────────────────────────────────────────────────────
export function SectionIntro({ eyebrow, title, lead, align = 'center', className = '' }) {
  return (
    <div
      className={`flex flex-col gap-y-4 max-w-[680px] ${align === 'center' ? 'mx-auto items-center text-center' : ''} ${className}`}
    >
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <Heading>{title}</Heading>
      {lead && <Lead>{lead}</Lead>}
    </div>
  )
}

export function PageHero({ eyebrow, title, lead, primary, secondary, align = 'center', children }) {
  const centered = align === 'center'
  return (
    <Container>
      <div className="pt-[48px] min-[720px]:pt-[72px] pb-[40px]">
        <div
          className={`max-w-[760px] flex flex-col gap-y-[22px] ${centered ? 'mx-auto items-center text-center' : ''}`}
        >
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          <Heading as="h1">{title}</Heading>
          {lead && <Lead>{lead}</Lead>}
          {(primary || secondary) && (
            <div className={`flex gap-x-3 mt-[6px] flex-wrap ${centered ? 'justify-center' : ''}`}>
              {primary && (
                <Button to={primary.to} variant="accent" lg>
                  {primary.label}
                </Button>
              )}
              {secondary && (
                <Button to={secondary.to} variant="secondary" lg>
                  {secondary.label}
                  <Icon name="arrow" size={16} />
                </Button>
              )}
            </div>
          )}
        </div>
        {children && <div className="mt-[56px]">{children}</div>}
      </div>
    </Container>
  )
}

export function FeatureGrid({ items, cols = 3 }) {
  return (
    <div
      className={`grid gap-5 grid-cols-1 sm:grid-cols-2 ${cols === 3 ? 'min-[960px]:grid-cols-3' : cols === 4 ? 'min-[960px]:grid-cols-4' : ''}`}
    >
      {items.map((it) => (
        <Card key={it.title} hover className="flex flex-col gap-y-4">
          <div className="flex items-center justify-between">
            <GlyphTile name={it.icon || 'check'} />
            {it.tag && <Badge tone="accent">{it.tag}</Badge>}
          </div>
          <div>
            <div className="text-[16px] font-semibold tracking-[-0.01em] text-[#0f172a]">{it.title}</div>
            <p className="mt-2 text-[14px] leading-[1.6] text-[#64748b]">{it.body}</p>
          </div>
        </Card>
      ))}
    </div>
  )
}

export function FeatureRow({ eyebrow, title, body, bullets = [], media, reverse = false }) {
  return (
    <div className="grid grid-cols-1 gap-10 items-center min-[960px]:grid-cols-2 min-[960px]:gap-16">
      <div className={reverse ? 'min-[960px]:order-2' : ''}>
        {eyebrow && <Eyebrow className="mt-2">{eyebrow}</Eyebrow>}
        <Heading as="h3" className="mt-4">
          {title}
        </Heading>
        {body && <Lead className="mt-4">{body}</Lead>}
        {bullets.length > 0 && (
          <ul className="list-none mt-6 p-0 flex flex-col gap-y-3">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-x-3 text-[15px] leading-[1.5] text-[#334155]">
                <span className="mt-px inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#eaf2fe] text-[#1D68D9] flex-none">
                  <Icon name="check" size={13} />
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>{media}</div>
    </div>
  )
}

export function StatBand({ stats }) {
  const n = stats.length === 3 ? 3 : 4
  return (
    <div
      className={`grid grid-cols-2 gap-px overflow-hidden rounded-[24px] border border-[#e6e9ef] bg-[#e6e9ef] ${n === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}
    >
      {stats.map((s, i) => (
        <div key={i} className="bg-white px-6 py-8 text-center">
          <div className="text-[34px] font-bold tracking-[-0.02em] text-[#0f172a] leading-none tabular-nums">{s.value}</div>
          <div className="mt-2 text-[13px] leading-[1.4] text-[#94a3b8]">{s.label}</div>
        </div>
      ))}
    </div>
  )
}

export function Steps({ items }) {
  return (
    <div className="flex flex-col gap-y-4">
      {items.map((it, i) => (
        <div key={it.title} className="flex gap-x-4 rounded-[18px] border border-[#e6e9ef] bg-white p-[22px]">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#0b1220] text-white text-[14px] font-bold flex-none">{i + 1}</span>
          <div>
            <div className="text-[16px] font-semibold tracking-[-0.01em] text-[#0f172a]">{it.title}</div>
            <p className="mt-1.5 text-[14px] leading-[1.6] text-[#64748b]">{it.body}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

export function Accordion({ items, className = '' }) {
  const [open, setOpen] = useState(null)
  return (
    <div className={`overflow-hidden rounded-[20px] border border-[#e6e9ef] bg-white ${className}`}>
      {items.map((it, i) => {
        const isOpen = open === i
        return (
          <div key={i} className="border-t border-[#eef1f6] first:border-t-0">
            <button
              type="button"
              className="flex w-full items-center justify-between gap-x-4 px-[22px] py-5 text-left bg-transparent border-none"
              onClick={() => setOpen(isOpen ? null : i)}
            >
              <span className="text-[15px] font-semibold leading-[1.45] text-[#0f172a]">{it.q}</span>
              <span
                className={`inline-flex items-center justify-center w-7 h-7 rounded-full border border-[#e6e9ef] text-[#64748b] flex-none transition-transform duration-200 ${isOpen ? 'rotate-45' : ''}`}
              >
                <Icon name="plus" size={16} />
              </span>
            </button>
            {isOpen && (
              <div className="pt-0 pr-12 pb-[22px] pl-[22px] text-[14px] leading-[1.65] text-[#64748b]">{it.a}</div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function Quote({ quote, name, role, className = '' }) {
  return (
    <Card className={className}>
      <div className="flex flex-col gap-y-5 h-full">
        <Icon name="sparkle" size={24} style={{ color: 'var(--mk-accent)' }} />
        <p className="text-[17px] leading-[1.55] tracking-[-0.01em] text-[#0f172a] m-0">“{quote}”</p>
        <div className="mt-auto flex items-center gap-x-3">
          <span className="inline-flex items-center justify-center w-[38px] h-[38px] rounded-full bg-[#0b1220] text-white text-[13px] font-bold flex-none">{(name || '?').slice(0, 1)}</span>
          <div>
            <div className="text-[13px] font-semibold text-[#0f172a]">{name}</div>
            <div className="text-[12px] text-[#94a3b8]">{role}</div>
          </div>
        </div>
      </div>
    </Card>
  )
}

export function LogoCloud({ label = 'Built for teams that sell every day', logos = [] }) {
  return (
    <div className="flex flex-col items-center gap-y-4 text-center py-7 border-t border-b border-[#eef1f6]">
      <span className="text-[12px] font-semibold tracking-[0.14em] uppercase text-[#94a3b8]">{label}</span>
      <div className="flex flex-wrap items-center justify-center gap-x-[22px] gap-y-[10px] text-[15px] font-semibold text-[#b4bcca]">
        {logos.map((l, i) => (
          <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 22 }}>
            {i > 0 && <span className="text-[#e6e9ef]">·</span>}
            {l}
          </span>
        ))}
      </div>
    </div>
  )
}

// ── Pricing ──────────────────────────────────────────────────────────────────
export function PricingTable({ plans }) {
  const cols = plans.length === 4 ? 4 : 3
  return (
    <div
      className={`grid gap-5 grid-cols-1 ${cols === 4 ? 'min-[960px]:grid-cols-4' : 'min-[960px]:grid-cols-3'}`}
    >
      {plans.map((plan) => (
        <div
          key={plan.name}
          className={`flex flex-col rounded-[22px] border bg-white p-7 ${plan.featured ? 'border-[#3C87F7] shadow-[0_12px_40px_rgba(60,135,247,0.16)] relative' : 'border-[#e6e9ef]'}`}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="text-[15px] font-bold text-[#0f172a]">{plan.name}</span>
            {plan.badge && <Badge tone="accent">{plan.badge}</Badge>}
          </div>
          <p className="mt-1.5 text-[13px] text-[#64748b] min-h-[34px]">{plan.tagline}</p>
          <div className="mt-[18px] flex items-baseline gap-x-1">
            <span className="text-[40px] font-bold tracking-[-0.02em] text-[#0f172a]">{plan.price}</span>
            {plan.period && <span className="text-[14px] text-[#94a3b8]">{plan.period}</span>}
          </div>
          <ul className="list-none mt-[22px] pt-[22px] border-t border-[#eef1f6] flex flex-col gap-y-3">
            {plan.features.map((f) => (
              <li key={f} className="flex items-start gap-x-2.5 text-[14px] leading-[1.5] text-[#334155]">
                <span className="mt-px inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#eaf2fe] text-[#1D68D9] flex-none">
                  <Icon name="check" size={12} />
                </span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6">
            <Button
              to={plan.cta?.to || '/signup'}
              variant={plan.featured ? 'accent' : 'secondary'}
              className="px-[26px] py-[14px] text-[15px]"
              style={{ width: '100%' }}
            >
              {plan.cta?.label || 'Get started'}
            </Button>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Timeline (changelog) ─────────────────────────────────────────────────────
export function Timeline({ items }) {
  return (
    <div className="flex flex-col gap-y-0">
      {items.map((it, i) => (
        <div
          key={i}
          className="grid grid-cols-1 gap-[10px] pb-10 relative min-[720px]:grid-cols-[160px_1fr] min-[720px]:gap-7"
        >
          <div className="relative">
            <div className="text-[13px] font-semibold text-[#94a3b8] static min-[720px]:sticky min-[720px]:top-[90px]">{it.date}</div>
          </div>
          <div className="pb-2 relative min-[720px]:border-l min-[720px]:border-[#e6e9ef] min-[720px]:pl-7 min-[720px]:before:content-[''] min-[720px]:before:absolute min-[720px]:before:left-[-5px] min-[720px]:before:top-[6px] min-[720px]:before:w-[9px] min-[720px]:before:h-[9px] min-[720px]:before:rounded-full min-[720px]:before:bg-[#3C87F7] min-[720px]:before:shadow-[0_0_0_4px_#fff]">
            {it.tag && (
              <div style={{ marginBottom: 8 }}>
                <Badge tone="accent">{it.tag}</Badge>
              </div>
            )}
            <div
              className="m-0 text-[22px] min-[720px]:text-[26px] font-semibold tracking-[-0.022em] leading-[1.15] text-[#0f172a]"
              style={{ fontSize: 20 }}
            >
              {it.title}
            </div>
            {it.body && <p className="mt-2 mb-0 text-[14px] leading-[1.6] text-[#64748b]">{it.body}</p>}
            {it.bullets && (
              <ul className="list-none mt-6 p-0 flex flex-col gap-y-3" style={{ marginTop: 14 }}>
                {it.bullets.map((b, j) => (
                  <li key={j} className="flex items-start gap-x-3 text-[15px] leading-[1.5] text-[#334155]">
                    <span className="mt-px inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#eaf2fe] text-[#1D68D9] flex-none">
                      <Icon name="check" size={13} />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Blog / posts ─────────────────────────────────────────────────────────────
export function PostGrid({ posts }) {
  return (
    <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 min-[960px]:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.title} hover>
          <div className="flex flex-col gap-y-[14px] h-full">
            <div
              className={`rounded-[14px] h-[168px] bg-gradient-to-br from-[#eaf2fe] to-[#dbe8ff] relative overflow-hidden ${i % 2 ? 'from-[#eef2f7] to-[#dfe6f0]' : ''}`}
            />
            <div className="flex items-center gap-x-2 text-[12px] text-[#94a3b8]">
              <Badge tone="accent">{post.tag}</Badge>
              <span>{post.readTime}</span>
            </div>
            <div className="text-[17px] font-semibold tracking-[-0.01em] text-[#0f172a] leading-[1.3]">{post.title}</div>
            <p className="text-[14px] leading-[1.6] text-[#64748b] m-0">{post.excerpt}</p>
            <div className="flex items-center gap-x-2 text-[12px] text-[#94a3b8]" style={{ marginTop: 'auto' }}>
              <span>{post.author}</span>
              <span>·</span>
              <span>{post.date}</span>
            </div>
          </div>
        </Card>
      ))}
    </div>
  )
}

// ── Careers / jobs ───────────────────────────────────────────────────────────
export function JobList({ groups }) {
  return (
    <div>
      {groups.map((group) => (
        <div key={group.team} className="mt-9 first:mt-0">
          <div className="text-[13px] font-bold tracking-[0.1em] uppercase text-[#94a3b8] mb-3">{group.team}</div>
          {group.roles.map((role) => (
            <Link
              key={role.title}
              to={role.to || '/contact'}
              className="flex items-center justify-between gap-x-4 px-[22px] py-5 border border-[#e6e9ef] rounded-[16px] bg-white transition-[border-color,box-shadow] duration-150 [&+&]:mt-[10px] hover:border-[#b4bcca] hover:shadow-[0_6px_20px_rgba(15,23,42,0.05)]"
            >
              <div>
                <div className="text-[16px] font-semibold text-[#0f172a]">{role.title}</div>
                <div className="text-[13px] text-[#94a3b8] mt-[3px]">
                  {role.location} · {role.type}
                </div>
              </div>
              <Icon name="arrow" size={18} style={{ color: 'var(--mk-faint)' }} />
            </Link>
          ))}
        </div>
      ))}
    </div>
  )
}

// ── Status ───────────────────────────────────────────────────────────────────
const STATUS_LABEL = {
  operational: 'Operational',
  degraded: 'Degraded',
  maintenance: 'Maintenance',
  outage: 'Outage',
}
export function StatusPill({ state = 'operational', label }) {
  return (
    <span
      className={`inline-flex items-center gap-x-2 rounded-full px-3 py-[5px] text-[12px] font-semibold ${state === 'degraded' ? 'bg-[#fffaeb] text-[#b54708]' : state === 'maintenance' ? 'bg-[#eff4ff] text-[#3538cd]' : state === 'outage' ? 'bg-[#fef3f2] text-[#b42318]' : 'bg-[#ecfdf3] text-[#067647]'}`}
    >
      <span
        className={`w-2 h-2 rounded-full ${state === 'degraded' ? 'bg-[#f79009]' : state === 'maintenance' ? 'bg-[#6172f3]' : state === 'outage' ? 'bg-[#f04438]' : 'bg-[#17b26a]'}`}
      />
      {label || STATUS_LABEL[state] || 'Operational'}
    </span>
  )
}

export function UptimeBar({ days = 90, incidents = {} }) {
  const cells = Array.from({ length: days }, (_, i) => incidents[i] || 'operational')
  return (
    <div className="flex items-end gap-x-[2px]">
      {cells.map((c, i) => (
        <span
          key={i}
          className={`flex-1 max-w-[7px] h-[30px] rounded-[2px] ${c === 'degraded' ? 'bg-[#f79009]' : c === 'outage' ? 'bg-[#f04438]' : 'bg-[#17b26a]'}`}
          title={`Day ${days - i}`}
        />
      ))}
    </div>
  )
}

// ── CTA band ─────────────────────────────────────────────────────────────────
export function CTASection({
  eyebrow = 'Get started',
  title,
  body,
  primary = { label: 'Start free', to: '/signup' },
  secondary = { label: 'Book a demo', to: '/demo' },
}) {
  return (
    <Section>
      <Container>
        <DarkPanel>
          <div className="py-[52px] px-[22px] text-center sm:py-[72px] sm:px-8">
            <div className="pointer-events-none absolute top-[-120px] left-1/2 -translate-x-1/2 w-[460px] h-[300px] rounded-full bg-[#3C87F7] opacity-[0.22] blur-[100px]" />
            <div className="relative max-w-[640px] mx-auto flex flex-col items-center gap-y-5">
              <Badge tone="dark">{eyebrow}</Badge>
              <h2 className="m-0 text-[30px] sm:text-[40px] font-semibold tracking-[-0.02em] leading-[1.08] text-white">{title}</h2>
              {body && <p className="m-0 text-[16px] leading-[1.6] text-[rgba(255,255,255,0.62)]">{body}</p>}
              <div className="flex gap-x-3 mt-[6px] flex-wrap justify-center">
                {primary && (
                  <Button to={primary.to} variant="white" lg>
                    {primary.label}
                  </Button>
                )}
                {secondary && (
                  <Button to={secondary.to} variant="outline-white" lg>
                    {secondary.label}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DarkPanel>
      </Container>
    </Section>
  )
}

// ── Prose / legal ────────────────────────────────────────────────────────────
export function Prose({ children, className = '' }) {
  return (
    <div
      className={`max-w-[760px] text-[#334155] [&_h2]:text-[22px] [&_h2]:font-semibold [&_h2]:tracking-[-0.01em] [&_h2]:text-[#0f172a] [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:text-[17px] [&_h3]:font-semibold [&_h3]:text-[#0f172a] [&_h3]:mt-[26px] [&_h3]:mb-2 [&_p]:text-[15px] [&_p]:leading-[1.7] [&_p]:text-[#334155] [&_p]:mb-[14px] [&_ul]:mb-4 [&_ul]:pl-5 [&_li]:text-[15px] [&_li]:leading-[1.7] [&_li]:text-[#334155] [&_li]:mb-1.5 [&_a]:text-[#1D68D9] [&_a]:font-medium [&_a:hover]:underline ${className}`}
    >
      {children}
    </div>
  )
}

export function LegalLayout({ toc = [], children }) {
  return (
    <div className="grid grid-cols-1 gap-10 min-[960px]:grid-cols-[220px_1fr] min-[960px]:gap-14">
      {toc.length > 0 && (
        <nav className="sticky top-[90px] self-start flex-col gap-y-2 hidden min-[960px]:flex [&_a]:text-[13px] [&_a]:text-[#64748b] [&_a]:py-1 [&_a:hover]:text-[#0f172a]">
          {toc.map((t) => (
            <a key={t.id} href={`#${t.id}`}>
              {t.label}
            </a>
          ))}
        </nav>
      )}
      <div>{children}</div>
    </div>
  )
}

// ── Product mock frames (lightweight CSS "screenshots") ──────────────────────
export function Mock({ kind = 'dashboard' }) {
  const bars = [62, 88, 45, 96, 72, 58, 80]
  if (kind === 'leads' || kind === 'inbox') {
    const rows =
      kind === 'inbox'
        ? ['Re: Pricing for the ops team', 'Following up — pilot scope', 'Security review + timeline', 'Demo scheduled']
        : ['Apex Analytics', 'Summit Health Systems', 'Ridgeline Logistics', 'Cornerstone Software']
    return (
      <div className="rounded-[18px] border border-[#e6e9ef] bg-[#fafbfd] shadow-[0_20px_50px_rgba(15,23,42,0.08)] overflow-hidden">
        <div className="flex items-center gap-x-[6px] px-[14px] py-3 border-b border-[#eef1f6] bg-white">
          <span className="w-[10px] h-[10px] rounded-full bg-[#e2e8f0]" />
          <span className="w-[10px] h-[10px] rounded-full bg-[#e2e8f0]" />
          <span className="w-[10px] h-[10px] rounded-full bg-[#e2e8f0]" />
        </div>
        <div className="p-5">
          <div className="flex gap-x-3 mb-4">
            <div className="flex-1 rounded-[12px] border border-[#eef1f6] bg-white px-[14px] py-3">
              <div className="text-[20px] font-bold text-[#0f172a]">10,942</div>
              <div className="text-[12px] text-[#94a3b8] mt-0.5">Verified leads</div>
            </div>
            <div className="flex-1 rounded-[12px] border border-[#eef1f6] bg-white px-[14px] py-3">
              <div className="text-[20px] font-bold text-[#0f172a]">1,204</div>
              <div className="text-[12px] text-[#94a3b8] mt-0.5">In sequence</div>
            </div>
          </div>
          {rows.map((r, i) => (
            <div
              key={r}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '11px 12px',
                border: '1px solid var(--mk-line-soft)',
                borderRadius: 10,
                marginBottom: 8,
                background: '#fff',
              }}
            >
              <span
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 8,
                  background: 'var(--mk-accent-tint)',
                  color: 'var(--mk-accent-dark)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 700,
                  flex: 'none',
                }}
              >
                {r.slice(0, 1)}
              </span>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--mk-ink)', flex: 1 }}>{r}</span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: i === 0 ? 'var(--mk-accent-dark)' : 'var(--mk-faint)',
                }}
              >
                {kind === 'inbox' ? ['2m', '18m', '1h', '3h'][i] : ['New', 'Contacted', 'Quoted', 'Won'][i]}
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }
  if (kind === 'sequence') {
    const steps = ['Intro email', 'Wait 2 days', 'Follow-up + case study', 'Wait 3 days', 'Break-up email']
    return (
      <div className="rounded-[18px] border border-[#e6e9ef] bg-[#fafbfd] shadow-[0_20px_50px_rgba(15,23,42,0.08)] overflow-hidden">
        <div className="flex items-center gap-x-[6px] px-[14px] py-3 border-b border-[#eef1f6] bg-white">
          <span className="w-[10px] h-[10px] rounded-full bg-[#e2e8f0]" />
          <span className="w-[10px] h-[10px] rounded-full bg-[#e2e8f0]" />
          <span className="w-[10px] h-[10px] rounded-full bg-[#e2e8f0]" />
        </div>
        <div className="p-5">
          {steps.map((s, i) => (
            <div
              key={s}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                border: '1px solid var(--mk-line-soft)',
                borderRadius: 10,
                marginBottom: 8,
                background: i % 2 ? 'var(--mk-surface-soft)' : '#fff',
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 999,
                  background: i % 2 ? 'var(--mk-surface)' : 'var(--mk-dark)',
                  color: i % 2 ? 'var(--mk-faint)' : '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 700,
                  flex: 'none',
                }}
              >
                {Math.floor(i / 2) + 1}
              </span>
              <span style={{ fontSize: 13, color: 'var(--mk-ink-soft)' }}>{s}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }
  // dashboard / analytics
  return (
    <div className="rounded-[18px] border border-[#e6e9ef] bg-[#fafbfd] shadow-[0_20px_50px_rgba(15,23,42,0.08)] overflow-hidden">
      <div className="flex items-center gap-x-[6px] px-[14px] py-3 border-b border-[#eef1f6] bg-white">
        <span className="w-[10px] h-[10px] rounded-full bg-[#e2e8f0]" />
        <span className="w-[10px] h-[10px] rounded-full bg-[#e2e8f0]" />
        <span className="w-[10px] h-[10px] rounded-full bg-[#e2e8f0]" />
      </div>
      <div className="p-5">
        <div className="flex gap-x-3 mb-4">
          <div className="flex-1 rounded-[12px] border border-[#eef1f6] bg-white px-[14px] py-3">
            <div className="text-[20px] font-bold text-[#0f172a]">$1.9M</div>
            <div className="text-[12px] text-[#94a3b8] mt-0.5">Pipeline</div>
          </div>
          <div className="flex-1 rounded-[12px] border border-[#eef1f6] bg-white px-[14px] py-3">
            <div className="text-[20px] font-bold text-[#0f172a]">32%</div>
            <div className="text-[12px] text-[#94a3b8] mt-0.5">Reply rate</div>
          </div>
          <div className="flex-1 rounded-[12px] border border-[#eef1f6] bg-white px-[14px] py-3">
            <div className="text-[20px] font-bold text-[#0f172a]">18</div>
            <div className="text-[12px] text-[#94a3b8] mt-0.5">Deals won</div>
          </div>
        </div>
        <div className="flex items-end gap-x-2.5 h-[160px] [&>span]:flex-1 [&>span]:rounded-t-[6px] [&>span]:bg-gradient-to-b [&>span]:from-[#5b9dff] [&>span]:to-[#3C87F7] [&>span]:opacity-90">
          {bars.map((h, i) => (
            <span key={i} style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    </div>
  )
}

// ── SEO head manager ─────────────────────────────────────────────────────────
export function Seo({ title, description, path, keywords, jsonLd }) {
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : ''
  useLayoutEffect(() => {
    const fullTitle = title
      ? `${title} · ${SITE_NAME}`
      : `${SITE_NAME} — Verified B2B leads, outreach, and pipeline`
    const url = SITE_URL + (path || (typeof window !== 'undefined' ? window.location.pathname : ''))
    const prevTitle = document.title
    document.title = fullTitle

    const created = []
    const upsert = (sel, make, apply) => {
      let el = document.head.querySelector(sel)
      if (!el) {
        el = make()
        document.head.appendChild(el)
        created.push(el)
      }
      apply(el)
    }
    const meta = (attr, key, content) => {
      if (!content) return
      upsert(
        `meta[${attr}="${key}"]`,
        () => {
          const m = document.createElement('meta')
          m.setAttribute(attr, key)
          return m
        },
        (m) => m.setAttribute('content', content),
      )
    }
    meta('name', 'description', description)
    meta('name', 'keywords', keywords)
    meta('property', 'og:title', fullTitle)
    meta('property', 'og:description', description)
    meta('property', 'og:type', 'website')
    meta('property', 'og:url', url)
    meta('property', 'og:site_name', SITE_NAME)
    meta('name', 'twitter:card', 'summary_large_image')
    meta('name', 'twitter:title', fullTitle)
    meta('name', 'twitter:description', description)
    upsert(
      'link[rel="canonical"]',
      () => {
        const l = document.createElement('link')
        l.setAttribute('rel', 'canonical')
        return l
      },
      (l) => l.setAttribute('href', url),
    )

    let script = null
    if (jsonLdStr) {
      script = document.createElement('script')
      script.type = 'application/ld+json'
      script.text = jsonLdStr
      document.head.appendChild(script)
    }
    return () => {
      document.title = prevTitle
      created.forEach((el) => el.remove())
      if (script) script.remove()
    }
  }, [title, description, path, keywords, jsonLdStr])
  return null
}

// ── Scroll management ────────────────────────────────────────────────────────
function ScrollManager() {
  const { pathname, hash } = useLocation()
  useLayoutEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1))
      if (el) {
        el.scrollIntoView({ block: 'start' })
        return
      }
    }
    window.scrollTo(0, 0)
  }, [pathname, hash])
  return null
}

// ── Header ───────────────────────────────────────────────────────────────────
const NAV = [
  { label: 'Features', to: '/features', key: 'features' },
  { label: 'Solutions', to: '/solutions', key: 'solutions' },
  { label: 'Integrations', to: '/integrations', key: 'integrations' },
  { label: 'Pricing', to: '/pricing', key: 'pricing' },
  { label: 'Security', to: '/security', key: 'security' },
]

export function MarketingHeader({ active }) {
  const [menu, setMenu] = useState(false)
  return (
    <header className="sticky top-0 z-40 w-full border-b border-[#eef1f6] bg-[rgba(255,255,255,0.82)] backdrop-blur-[12px] backdrop-saturate-[1.8]">
      <Container>
        <div className="flex h-16 items-center justify-between relative">
          <Link
            to="/"
            className="inline-flex items-center gap-x-2.5 font-bold text-[16px] tracking-[-0.01em] text-[#0f172a]"
            aria-label="Revailo Sales home"
          >
            <img src="/logo-on-light.png?v=5" alt="" className="w-[26px] h-[26px] flex-none" />
            <span>Revailo Sales</span>
          </Link>

          <nav className="absolute left-1/2 -translate-x-1/2 flex items-center gap-x-0.5 p-1 rounded-full bg-[#f6f8fb] max-[900px]:hidden">
            {NAV.map((n) => (
              <Link
                key={n.key}
                to={n.to}
                className={`rounded-full px-[14px] py-[7px] text-[13px] font-medium transition-[color,background] duration-150 ${active === n.key ? 'text-[#0f172a] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.08)]' : 'text-[#64748b] hover:text-[#0f172a]'}`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-x-2.5">
            <Link to="/login" className="text-[13px] font-medium text-[#64748b] px-2.5 py-2 hover:text-[#0f172a] max-[900px]:hidden">
              Sign in
            </Link>
            <Button to="/signup" variant="primary" className="" style={{ padding: '9px 16px', fontSize: 13 }}>
              Get started
            </Button>
            <button
              type="button"
              className="hidden max-[900px]:inline-flex items-center justify-center w-[38px] h-[38px] rounded-full border border-[#e6e9ef] bg-white text-[#0f172a]"
              onClick={() => setMenu((v) => !v)}
              aria-label="Menu"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7">
                <line x1="4" y1="8" x2="20" y2="8" />
                <line x1="4" y1="16" x2="20" y2="16" />
              </svg>
            </button>
          </div>
        </div>
      </Container>

      {menu && (
        <div className="border-t border-[#eef1f6] bg-white pt-2 px-6 pb-4 flex flex-col gap-y-0.5">
          {NAV.map((n) => (
            <Link
              key={n.key}
              to={n.to}
              onClick={() => setMenu(false)}
              className={`rounded-[10px] px-3 py-[11px] text-[14px] ${active === n.key ? 'bg-[#f6f8fb] font-semibold text-[#0f172a]' : 'text-[#334155]'}`}
            >
              {n.label}
            </Link>
          ))}
          <Link
            to="/login"
            onClick={() => setMenu(false)}
            className="rounded-[10px] px-3 py-[11px] text-[14px] text-[#334155]"
          >
            Sign in
          </Link>
        </div>
      )}
    </header>
  )
}

// ── Footer ───────────────────────────────────────────────────────────────────
const FOOTER_COLS = [
  {
    title: 'Product',
    links: [
      { label: 'Features', to: '/features' },
      { label: 'Integrations', to: '/integrations' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'How it works', to: '/how-it-works' },
      { label: 'Changelog', to: '/changelog' },
      { label: 'System status', to: '/status' },
    ],
  },
  {
    title: 'Solutions',
    links: [
      { label: 'Vertical sales teams', to: '/solutions#verticals' },
      { label: 'Services businesses', to: '/solutions#general' },
      { label: 'Commercial builders', to: '/solutions#commercial' },
      { label: 'Agencies & resellers', to: '/solutions#agencies' },
      { label: 'Regional expansion', to: '/solutions#expansion' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', to: '/about' },
      { label: 'Customers', to: '/customers' },
      { label: 'Careers', to: '/careers' },
      { label: 'Blog', to: '/blog' },
      { label: 'Contact sales', to: '/contact' },
      { label: 'Book a demo', to: '/demo' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Security', to: '/security' },
      { label: 'FAQ', to: '/faq' },
      { label: 'System status', to: '/status' },
      { label: 'Sign in', to: '/login' },
      { label: 'Sign up', to: '/signup' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy notice', to: '/privacy' },
      { label: 'Terms of service', to: '/terms' },
      { label: 'Data processing', to: '/dpa' },
      { label: 'Contact', to: '/contact' },
    ],
  },
]

export function MarketingFooter() {
  const year = 2026
  const socials = [
    { name: 'x', href: 'https://x.com', d: 'M4 4l7 8m0 0l7 8M11 12l7-8M11 12l-7 8' },
    { name: 'linkedin', href: 'https://linkedin.com', d: 'M6 9v8M6 6v.01M10 17v-4a2 2 0 0 1 4 0v4M10 9v8' },
    { name: 'youtube', href: 'https://youtube.com', d: 'M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8zM10 9l5 3-5 3V9z' },
  ]
  return (
    <footer className="border-t border-[#eef1f6] bg-[#fafbfd]">
      <Container>
        <div className="py-14">
          <div className="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 min-[960px]:grid-cols-[1.5fr_repeat(4,1fr)]">
            <div className="col-span-full min-[960px]:col-auto">
              <Link to="/" className="inline-flex items-center gap-x-2.5 font-bold text-[16px] tracking-[-0.01em] text-[#0f172a]">
                <img src="/logo-on-light.png?v=5" alt="" className="w-[26px] h-[26px] flex-none" />
                <span>Revailo Sales</span>
              </Link>
              <p className="mt-4 max-w-[260px] text-[13px] leading-[1.6] text-[#94a3b8]">
                The B2B lead engine — 10,900+ verified companies,
                outreach, and pipeline in one workspace.
              </p>
              <div className="flex gap-x-2 mt-5 [&_a]:inline-flex [&_a]:items-center [&_a]:justify-center [&_a]:w-8 [&_a]:h-8 [&_a]:rounded-full [&_a]:border [&_a]:border-[#e6e9ef] [&_a]:text-[#94a3b8] [&_a:hover]:text-[#0f172a] [&_a:hover]:border-[#b4bcca]">
                {socials.map((s) => (
                  <a key={s.name} href={s.href} target="_blank" rel="noreferrer" aria-label={s.name}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d={s.d} />
                    </svg>
                  </a>
                ))}
              </div>
            </div>
            {FOOTER_COLS.map((col) => (
              <div key={col.title}>
                <div className="text-[12px] font-bold tracking-[0.12em] uppercase text-[#94a3b8]">{col.title}</div>
                <ul className="list-none mt-4 p-0 flex flex-col gap-y-2.5 [&_a]:text-[13px] [&_a]:text-[#64748b] [&_a:hover]:text-[#0f172a]">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <Link to={l.to}>{l.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 pt-6 border-t border-[#eef1f6] flex flex-col gap-3 items-start justify-between sm:flex-row sm:items-center">
            <p className="text-[12px] text-[#94a3b8]">© {year} Revailo Sales. All rights reserved.</p>
            <Link to="/status" className="inline-flex items-center gap-x-2 text-[12px] text-[#94a3b8] hover:text-[#0f172a]">
              <span className="w-2 h-2 rounded-full" style={{ background: '#17b26a', width: 8, height: 8, borderRadius: 999 }} />
              All systems operational
            </Link>
          </div>
        </div>
      </Container>
    </footer>
  )
}

// ── Shell ────────────────────────────────────────────────────────────────────
export function MarketingShell({ active, children }) {
  return (
    <div className="mk-root">
      <ScrollManager />
      <MarketingHeader active={active} />
      <main>{children}</main>
      <MarketingFooter />
    </div>
  )
}
